import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  BarChart3,
  Clock,
  CheckCircle2,
  Settings,
  Loader2,
  Search,
  RefreshCw,
  Award,
  Target,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

interface Analyst {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  isActive: boolean;
  isSupervisor: boolean;
  maxDailyAssignments: number;
  currentAssignments: number;
  totalAnalyzed: number;
  totalApproved: number;
  totalRejected: number;
  avgAnalysisTimeMinutes: number;
  approvalRate: number;
  lastActivityAt: string | null;
}

interface AnalystProductivity {
  analystId: string;
  analystName: string;
  period: string;
  analyzed: number;
  approved: number;
  rejected: number;
  avgTimeMinutes: number;
  approvalRate: number;
}

interface AssignmentStats {
  totalPending: number;
  totalAssigned: number;
  avgWaitTimeHours: number;
  autoAssignEnabled: boolean;
}

export function KycAnalystManagement() {
  const [activeTab, setActiveTab] = useState<'analysts' | 'productivity' | 'settings'>('analysts');
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  const { data: analysts, isLoading: loadingAnalysts, refetch } = useQuery<Analyst[]>({
    queryKey: ['kyc-analysts'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/analysts');
      return response.data;
    },
  });

  const { data: stats } = useQuery<AssignmentStats>({
    queryKey: ['kyc-assignment-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/analysts/stats');
      return response.data;
    },
  });

  const toggleAnalystMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await api.patch(`/admin/kyc/analysts/${id}`, { isActive });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-analysts'] });
    },
  });

  const filteredAnalysts = analysts?.filter(a =>
    a.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'analysts' as const, label: 'Analistas', icon: Users },
    { id: 'productivity' as const, label: 'Produtividade', icon: BarChart3 },
    { id: 'settings' as const, label: 'Configurações', icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-3">
            <Users className="w-7 h-7 text-primary-400" />
            Gestão de Analistas KYC
          </h1>
          <p className="text-surface-400 mt-1">
            Gerencie analistas, atribuições e produtividade
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 bg-surface-800 rounded-lg text-surface-400 hover:text-surface-100 transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Analistas Ativos"
          value={analysts?.filter(a => a.isActive).length || 0}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="KYCs Pendentes"
          value={stats?.totalPending || 0}
          icon={Clock}
          color="yellow"
        />
        <StatCard
          title="Em Análise"
          value={stats?.totalAssigned || 0}
          icon={Target}
          color="cyan"
        />
        <StatCard
          title="Tempo Médio Espera"
          value={`${stats?.avgWaitTimeHours?.toFixed(1) || 0}h`}
          icon={Clock}
          color="purple"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-700 pb-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "px-4 py-2 rounded-t-lg font-medium flex items-center gap-2 transition-colors",
                activeTab === tab.id
                  ? "bg-surface-800 text-primary-400 border-b-2 border-primary-400"
                  : "text-surface-400 hover:text-surface-100"
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="glass rounded-xl p-6">
        {activeTab === 'analysts' && (
          <AnalystsTab
            analysts={filteredAnalysts || []}
            isLoading={loadingAnalysts}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onToggle={(id, isActive) => toggleAnalystMutation.mutate({ id, isActive })}
          />
        )}
        {activeTab === 'productivity' && <ProductivityTab />}
        {activeTab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: number | string;
  icon: React.ElementType;
  color: 'blue' | 'green' | 'cyan' | 'purple' | 'yellow';
}) {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    green: 'text-green-400 bg-green-500/10',
    cyan: 'text-cyan-400 bg-cyan-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
    yellow: 'text-yellow-400 bg-yellow-500/10',
  };

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-surface-400">{title}</span>
        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", colorClasses[color])}>
          <Icon className={cn("w-4 h-4", colorClasses[color].split(' ')[0])} />
        </div>
      </div>
      <span className="text-2xl font-bold text-surface-100">{value}</span>
    </div>
  );
}

function AnalystsTab({
  analysts,
  isLoading,
  searchTerm,
  onSearchChange,
  onToggle,
}: {
  analysts: Analyst[];
  isLoading: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onToggle: (id: string, isActive: boolean) => void;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar analista..."
            className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          />
        </div>
      </div>

      {/* Analysts Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-surface-700">
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Analista</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Atribuições</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Analisados</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Taxa Aprovação</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Tempo Médio</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Status</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Ações</th>
            </tr>
          </thead>
          <tbody>
            {analysts.map((analyst) => (
              <tr key={analyst.id} className="border-b border-surface-800 hover:bg-surface-800/50">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-400">
                        {analyst.userName.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-surface-100 font-medium">{analyst.userName}</p>
                      <p className="text-xs text-surface-500">{analyst.userEmail}</p>
                    </div>
                    {analyst.isSupervisor && (
                      <span title="Supervisor">
                        <Award className="w-4 h-4 text-yellow-400" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className="text-surface-100">
                    {analyst.currentAssignments} / {analyst.maxDailyAssignments}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className="text-surface-100">{analyst.totalAnalyzed}</span>
                    <span className="text-xs text-green-400">({analyst.totalApproved} ✓)</span>
                    <span className="text-xs text-red-400">({analyst.totalRejected} ✗)</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-2 bg-surface-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${analyst.approvalRate}%` }}
                      />
                    </div>
                    <span className="text-sm text-surface-400">{analyst.approvalRate.toFixed(1)}%</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className="text-surface-100">{analyst.avgAnalysisTimeMinutes.toFixed(0)} min</span>
                </td>
                <td className="py-3 px-4">
                  <span className={cn(
                    "px-2 py-1 rounded-full text-xs font-medium",
                    analyst.isActive
                      ? "bg-green-500/20 text-green-400"
                      : "bg-surface-700 text-surface-400"
                  )}>
                    {analyst.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => onToggle(analyst.id, !analyst.isActive)}
                    className={cn(
                      "px-3 py-1 rounded-lg text-sm font-medium transition-colors",
                      analyst.isActive
                        ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    )}
                  >
                    {analyst.isActive ? 'Desativar' : 'Ativar'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {analysts.length === 0 && (
        <div className="text-center py-12 text-surface-500">
          Nenhum analista encontrado
        </div>
      )}
    </div>
  );
}

function ProductivityTab() {
  const [period, setPeriod] = useState<'7d' | '30d'>('7d');

  const { data: productivity, isLoading } = useQuery<AnalystProductivity[]>({
    queryKey: ['kyc-analyst-productivity', period],
    queryFn: async () => {
      const response = await api.get(`/admin/kyc/analysts/productivity?period=${period}`);
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-surface-100">Produtividade por Analista</h2>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as '7d' | '30d')}
          className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
        >
          <option value="7d">Últimos 7 dias</option>
          <option value="30d">Últimos 30 dias</option>
        </select>
      </div>

      {productivity && productivity.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={productivity}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="analystName" stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" fontSize={12} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                labelStyle={{ color: '#f3f4f6' }}
              />
              <Bar dataKey="approved" name="Aprovados" fill="#22c55e" />
              <Bar dataKey="rejected" name="Rejeitados" fill="#ef4444" />
            </BarChart>
          </ResponsiveContainer>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Analista</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Analisados</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Aprovados</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Rejeitados</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Taxa</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-surface-400">Tempo Médio</th>
                </tr>
              </thead>
              <tbody>
                {productivity.map((p) => (
                  <tr key={p.analystId} className="border-b border-surface-800">
                    <td className="py-3 px-4 text-surface-100">{p.analystName}</td>
                    <td className="py-3 px-4 text-surface-100">{p.analyzed}</td>
                    <td className="py-3 px-4 text-green-400">{p.approved}</td>
                    <td className="py-3 px-4 text-red-400">{p.rejected}</td>
                    <td className="py-3 px-4 text-surface-100">{p.approvalRate.toFixed(1)}%</td>
                    <td className="py-3 px-4 text-surface-100">{p.avgTimeMinutes.toFixed(0)} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-surface-500">
          Nenhum dado de produtividade disponível
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const queryClient = useQueryClient();
  const [autoAssign, setAutoAssign] = useState(true);
  const [maxPerAnalyst, setMaxPerAnalyst] = useState(20);

  const saveMutation = useMutation({
    mutationFn: async (data: { autoAssignEnabled: boolean; maxDailyPerAnalyst: number }) => {
      const response = await api.put('/admin/kyc/analysts/settings', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-assignment-stats'] });
    },
  });

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-surface-100">Configurações de Atribuição</h2>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-surface-800 rounded-lg">
          <div>
            <h3 className="font-medium text-surface-100">Atribuição Automática</h3>
            <p className="text-sm text-surface-400">
              Distribuir automaticamente KYCs pendentes entre analistas ativos
            </p>
          </div>
          <button
            onClick={() => setAutoAssign(!autoAssign)}
            className={cn(
              "w-12 h-6 rounded-full transition-colors relative",
              autoAssign ? "bg-green-500" : "bg-surface-600"
            )}
          >
            <div className={cn(
              "w-5 h-5 rounded-full bg-white absolute top-0.5 transition-transform",
              autoAssign ? "translate-x-6" : "translate-x-0.5"
            )} />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-surface-300 mb-2">
            Máximo de Atribuições por Analista (diário)
          </label>
          <input
            type="number"
            value={maxPerAnalyst}
            onChange={(e) => setMaxPerAnalyst(Number(e.target.value))}
            className="w-full px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
            min="1"
            max="100"
          />
          <p className="text-xs text-surface-500 mt-1">
            Limite máximo de KYCs que cada analista pode receber por dia
          </p>
        </div>

        <button
          onClick={() => saveMutation.mutate({ autoAssignEnabled: autoAssign, maxDailyPerAnalyst: maxPerAnalyst })}
          disabled={saveMutation.isPending}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
        >
          {saveMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4" />
          )}
          Salvar Configurações
        </button>
      </div>
    </div>
  );
}

export default KycAnalystManagement;
